import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getExchangeRates } from '../../../shared/api/exchangeRates.api'
import { toMatrixRates, type MatrixRates } from '../utils/convertQuoteToMatrixField'
import type { PricingReadiness } from './useRequestRelatedData'

/**
 * Курсы валют для перевода цены квоты в поле цены матрицы позиции.
 *
 * `rates === null` — справочник ещё грузится либо не загрузился: считать и
 * тем более писать цену в БД нельзя (раньше подставлялись выдуманные
 * 90 / 98 / 12.5, и позиция уезжала PATCH-ом с ценой по несуществующему
 * курсу). Состояние показываем плашкой PricingNotReady — тот же гейт, что у
 * карточки заявки (см. useRequestRelatedData).
 */
export function useMatrixRates(): { rates: MatrixRates | null; pricing: PricingReadiness } {
  const ratesQuery = useQuery({
    queryKey: ['exchange-rates'],
    queryFn: getExchangeRates,
    staleTime: 5 * 60 * 1000,
  })

  // Мемоизация обязательна: toMatrixRates возвращает новый литерал на каждый
  // вызов, и без useMemo ссылка `rates` меняется при любом ререндере. Потребитель
  // держит её в deps эффекта авто-выбора цены (RequestItemPage) — нестабильная
  // ссылка перезапускала эффект и отправляла авто-PATCH цены дважды.
  const rates = useMemo(() => toMatrixRates(ratesQuery.data), [ratesQuery.data])

  const { isLoading, isError, refetch } = ratesQuery
  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  const pricing = useMemo<PricingReadiness>(
    () => ({ ready: rates !== null, isLoading, isError, retry }),
    [rates, isLoading, isError, retry],
  )

  return { rates, pricing }
}
